import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useDemo } from '../../context/DemoContext';
import { toast } from 'sonner';
import OnChainReceipt, { fireOnChainToast } from '../OnChainReceipt';
import { 
  Flame, Timer, Zap, Trophy, Skull, Star, Shield, Wallet,
  ChevronRight, RotateCcw, TrendingUp, ChevronDown, Target
} from 'lucide-react';
import { CINEMATIC_KEYFRAMES, ScanlineOverlay, NoiseOverlay, GasParticles, ToxicDrips, SewerFrame, GlitchTitle, StatusBar, TotalFeesCounter } from './CinematicStyles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import gasCanister from '../../assets/slots/gas_canister.png';
import goldenGremlin from '../../assets/slots/gfg_coin.png';
import diamond from '../../assets/slots/diamond.png';
import gasBomb from '../../assets/slots/gas_bomb.png';
import whackBgImage from '../../assets/games/whack_a_gremlin.jpg';

const GREMLIN_TYPES = {
  gas: { id: 'gas', name: 'Fee Gremlin', image: gasCanister, points: 100, fees: 80, color: '#ff8c00', spawnWeight: 50 },
  golden: { id: 'golden', name: 'Golden Gremlin', image: goldenGremlin, points: 500, fees: 350, color: '#ff8c00', spawnWeight: 10 },
  diamond: { id: 'diamond', name: 'Diamond Gremlin', image: diamond, points: 1000, fees: 800, color: '#ff8c00', spawnWeight: 5 },
  penalty: { id: 'penalty', name: 'Penalty Gremlin', image: gasBomb, points: -300, fees: -50, color: '#ef4444', spawnWeight: 20 },
  neutral: { id: 'neutral', name: 'Neutral Gremlin', image: gasCanister, points: 25, fees: 20, color: '#6b7280', spawnWeight: 15 },
};

const DIFFICULTY_SETTINGS = {
  normal: {
    name: 'Normal', description: 'Learn the ropes',
    roundDuration: 45, spawnInterval: [800, 1500], popDuration: [1200, 2000],
    maxActive: 2, penaltyWeight: 15, feesMultiplier: 1.0,
    color: '#ff8c00', bgColor: 'rgba(255, 140, 0, 0.1)', borderColor: 'rgba(255, 140, 0, 0.3)',
  },
  hard: {
    name: 'Hard', description: 'Faster spawns, more traps',
    roundDuration: 45, spawnInterval: [500, 1000], popDuration: [800, 1400],
    maxActive: 3, penaltyWeight: 25, feesMultiplier: 2.0,
    color: '#cc5500', bgColor: 'rgba(168, 85, 247, 0.1)', borderColor: 'rgba(168, 85, 247, 0.3)',
  },
  nightmare: {
    name: 'Nightmare', description: 'Inhuman reflexes only',
    roundDuration: 60, spawnInterval: [280, 500], popDuration: [420, 750],
    maxActive: 4, penaltyWeight: 35, feesMultiplier: 5.0, hasBoss: true,
    color: '#ff8c00', secondaryColor: '#cc5500',
    bgColor: 'rgba(255, 140, 0, 0.08)', borderColor: 'rgba(255, 140, 0, 0.5)',
    glowColor: 'rgba(255, 140, 0, 0.7)', glowColorSecondary: 'rgba(168, 85, 247, 0.6)',
  },
};

const HOLE_POSITIONS = [
  { top: '29%', left: '27%' }, { top: '29%', left: '50%' }, { top: '29%', left: '73%' },
  { top: '46%', left: '27%' }, { top: '46%', left: '50%' }, { top: '46%', left: '73%' },
  { top: '63%', left: '27%' }, { top: '63%', left: '50%' }, { top: '63%', left: '73%' },
];

// Carved Roman numerals on each pit's rim — lore-themed slot identifiers (I-IX)
const PIT_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];

const MAX_CLICKS_PER_SECOND = 8;

export default function WhackAGremlin({ onWin, onBack, stakerMultiplier = 1 }) {
  const { publicKey, connected: walletConnected } = useWallet();
  const { isDemo, demoWallet, isLive } = useDemo();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : undefined);
  
  const [gameState, setGameState] = useState('menu');
  const [difficulty, setDifficulty] = useState('normal');
  const [score, setScore] = useState(0);
  const [feesEarned, setFeesEarned] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [gremlins, setGremlins] = useState(Array(9).fill(null));
  const [hits, setHits] = useState({ total: 0, correct: 0, penalty: 0 });
  const [lastHit, setLastHit] = useState(null);
  const [endedPhase, setEndedPhase] = useState('victory'); // 'victory' | 'stats'
  
  const [userFeesBalance, setUserFeesBalance] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  
  const clickTimestamps = useRef([]);
  const roundStartTime = useRef(null);
  const totalClicks = useRef(0);
  const holeCooldownRef = useRef({});
  const spawnTimerRef = useRef(null);
  const gameTimerRef = useRef(null);
  const sessionTokenRef = useRef(null);
  
  const settings = DIFFICULTY_SETTINGS[difficulty];

  useEffect(() => { if (walletAddress) fetchUserData(); }, [walletAddress]);

  const fetchUserData = async () => {
    if (!walletAddress) return;
    try {
      const res = await fetch(`${API_URL}/api/whack/user/${walletAddress}`);
      if (res.ok) {
        const data = await res.json();
        setUserFeesBalance(data.fee_balance || 0);
        setHighScore(data.high_score || 0);
      }
    } catch (err) { console.error('Failed to fetch user data:', err); }
  };

  useEffect(() => { fetchLeaderboard(); }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/whack/leaderboard`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) { console.error('Failed to fetch leaderboard:', err); }
  };

  const getRandomGremlinType = useCallback(() => {
    const types = Object.values(GREMLIN_TYPES);
    const adjustedTypes = types.map(t => ({ ...t, weight: t.id === 'penalty' ? settings.penaltyWeight : t.spawnWeight }));
    const totalWeight = adjustedTypes.reduce((sum, t) => sum + t.weight, 0);
    let random = Math.random() * totalWeight;
    for (const type of adjustedTypes) { random -= type.weight; if (random <= 0) return type; }
    return GREMLIN_TYPES.gas;
  }, [settings]);

  const spawnGremlin = useCallback(() => {
    if (gameState !== 'playing') return;
    const now = Date.now();
    setGremlins(prev => {
      const activeCount = prev.filter(g => g !== null).length;
      if (activeCount >= settings.maxActive) return prev;
      const emptyHoles = prev.map((g, i) => g === null && (now - (holeCooldownRef.current[i] || 0)) > 300 ? i : -1).filter(i => i >= 0);
      if (emptyHoles.length === 0) return prev;
      const holeIndex = emptyHoles[Math.floor(Math.random() * emptyHoles.length)];
      const gremlinType = getRandomGremlinType();
      const [minPop, maxPop] = settings.popDuration;
      const popDuration = minPop + Math.random() * (maxPop - minPop);
      const spawnTime = now + Math.random();
      const newGremlins = [...prev];
      newGremlins[holeIndex] = { ...gremlinType, spawnTime, popDuration };
      setTimeout(() => {
        holeCooldownRef.current[holeIndex] = Date.now();
        setGremlins(current => {
          const updated = [...current];
          if (updated[holeIndex]?.spawnTime === spawnTime) {
            updated[holeIndex] = null;
          }
          return updated;
        });
      }, popDuration);
      return newGremlins;
    });
  }, [gameState, settings, getRandomGremlinType]);

  // Spawn loop — simple interval, no recursive chain
  useEffect(() => {
    if (gameState !== 'playing') return;
    spawnGremlin();
    const [minInterval, maxInterval] = settings.spawnInterval;
    const avgInterval = (minInterval + maxInterval) / 2;
    const id = setInterval(spawnGremlin, avgInterval);
    return () => clearInterval(id);
  }, [gameState, settings, spawnGremlin]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    gameTimerRef.current = setInterval(() => {
      setTimeLeft(prev => { if (prev <= 1) { endGameRef.current?.(); return 0; } return prev - 1; });
    }, 1000);
    return () => { if (gameTimerRef.current) clearInterval(gameTimerRef.current); };
  }, [gameState]);

  const validateClick = useCallback(() => {
    const now = Date.now();
    clickTimestamps.current = clickTimestamps.current.filter(t => now - t < 1000);
    if (clickTimestamps.current.length >= MAX_CLICKS_PER_SECOND) return false;
    clickTimestamps.current.push(now);
    totalClicks.current++;
    return true;
  }, []);

  const whackGremlin = useCallback((index, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (gameState !== 'playing') return;
    if (!validateClick()) return;
    const gremlin = gremlins[index];
    if (!gremlin) return;
    setGremlins(prev => { const updated = [...prev]; updated[index] = null; return updated; });
    const difficultyMultiplier = settings.feesMultiplier;
    // Fees are driven by combo: tiny base + combo is the main scaler
    const comboFeeMultiplier = 0.1 + (combo * 0.3);
    const pointsMultiplier = 1 + (combo * 0.15);
    const pointsEarned = Math.floor(gremlin.points * pointsMultiplier);
    const feesEarnedThisHit = Math.max(0, Math.floor(gremlin.fees * comboFeeMultiplier * difficultyMultiplier * stakerMultiplier));
    setScore(prev => Math.max(0, prev + pointsEarned));
    if (gremlin.fees > 0) setFeesEarned(prev => prev + feesEarnedThisHit);
    if (gremlin.id === 'penalty') {
      setCombo(0);
      setHits(prev => ({ ...prev, total: prev.total + 1, penalty: prev.penalty + 1 }));
    } else {
      setCombo(prev => { const newCombo = Math.min(prev + 1, 50); setMaxCombo(current => Math.max(current, newCombo)); return newCombo; });
      setHits(prev => ({ ...prev, total: prev.total + 1, correct: prev.correct + 1 }));
    }
    setLastHit({ index, points: pointsEarned, fees: feesEarnedThisHit, type: gremlin.id, color: gremlin.color });
    setTimeout(() => setLastHit(null), 500);
  }, [gameState, gremlins, combo, settings, stakerMultiplier, validateClick]);

  const startGame = useCallback(async () => {
    // Request game session token
    if (walletAddress) {
      try {
        const res = await fetch(`${API_URL}/api/arcade/start-session`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: walletAddress, game_type: 'whack_a_gremlin' }),
        });
        if (res.ok) { const data = await res.json(); sessionTokenRef.current = data.session_token; }
      } catch (err) { console.error('Session start failed:', err); }
    }
    setGameState('playing');
    setScore(0); setFeesEarned(0); setCombo(0); setMaxCombo(0);
    setTimeLeft(settings.roundDuration);
    setGremlins(Array(9).fill(null));
    setHits({ total: 0, correct: 0, penalty: 0 });
    setLastHit(null);
    clickTimestamps.current = []; roundStartTime.current = Date.now(); totalClicks.current = 0;
  }, [settings, walletAddress]);

  const [feesClaimed, setFeesClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [onchainReceipt, setOnchainReceipt] = useState(null);

  // Ref to always hold latest endGame — avoids stale closure in setInterval
  const endGameRef = useRef(null);

  const endGame = useCallback(async () => {
    setGameState('ended');
    setEndedPhase('victory');
    setFeesClaimed(false); setClaiming(false);
    if (spawnTimerRef.current) clearTimeout(spawnTimerRef.current);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    const roundDuration = (Date.now() - roundStartTime.current) / 1000;
    const maxPossibleClicks = roundDuration * MAX_CLICKS_PER_SECOND;
    if (walletAddress && totalClicks.current <= maxPossibleClicks) {
      try {
        const res = await fetch(`${API_URL}/api/whack/submit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: walletAddress, score, gas_earned: feesEarned, difficulty, round_duration: roundDuration, total_clicks: totalClicks.current, hits, max_combo: maxCombo, session_token: sessionTokenRef.current, live_mode: !!isLive }),
        });
        if (res.ok) {
          const data = await res.json();
          setUserFeesBalance(data.new_gas_balance || userFeesBalance + feesEarned);
          if (score > highScore) setHighScore(score);
          fetchLeaderboard();
          // Fees already credited by whack/submit — auto-claim
          if (data.fees_credited) {
            setFeesClaimed(true);
            sessionTokenRef.current = null;
          }
          if (data.onchain) {
            setOnchainReceipt(data.onchain);
            fireOnChainToast(toast, data.onchain);
          }
        }
      } catch (err) { console.error('Failed to submit score:', err); }
    }
    if (onWin && feesEarned > 0) onWin(feesEarned, { score, maxCombo, difficulty });
  }, [walletAddress, score, feesEarned, difficulty, hits, maxCombo, onWin, highScore, userFeesBalance]);

  // Keep ref in sync with latest endGame
  useEffect(() => { endGameRef.current = endGame; }, [endGame]);

  const claimFees = useCallback(async () => {
    if (!walletAddress || feesClaimed || feesEarned <= 0) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/arcade/score`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, game_type: 'whack_a_gremlin', fees_earned: feesEarned, game_data: { gremlins_hit: hits.correct, max_combo: maxCombo, difficulty }, session_token: sessionTokenRef.current }),
      });
      if (res.ok) { setFeesClaimed(true); sessionTokenRef.current = null; }
    } catch (err) { console.error('Failed to claim fees:', err); }
    setClaiming(false);
  }, [walletAddress, feesClaimed, feesEarned, hits, maxCombo, difficulty]);

  // Victory cinematic → stats transition
  useEffect(() => {
    if (gameState === 'ended' && endedPhase === 'victory') {
      const t = setTimeout(() => setEndedPhase('stats'), 2800);
      return () => clearTimeout(t);
    }
  }, [gameState, endedPhase]);

  // ============ CINEMATIC MENU STATE ============
  if (gameState === 'menu') {
    const MENU_COLOR = '#ff8c00';
    const MENU_DIM = 'rgba(255,140,0,';
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="whack-game-menu"
        style={{ background: 'radial-gradient(ellipse at 50% 80%, #1a1505 0%, #0d0b02 30%, #050402 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}</style>
        <ScanlineOverlay />
        <NoiseOverlay />
        <GasParticles count={20} color="molten" />
        <ToxicDrips count={7} color={MENU_COLOR} />
        <SewerFrame color={MENU_DIM} />

        {/* Sewer pipe decoration - horizontal lines */}
        <div className="absolute inset-0 pointer-events-none z-5">
          {[20, 80].map(top => (
            <div key={top} className="absolute left-0 right-0" style={{
              top: `${top}%`,
              height: '1px',
              background: `linear-gradient(90deg, transparent 0%, ${MENU_DIM}0.08) 20%, ${MENU_DIM}0.03) 50%, ${MENU_DIM}0.08) 80%, transparent 100%)`,
            }} />
          ))}
        </div>

        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">

          <GlitchTitle text="SNATCH-A-BAG" subtitle="the sewers are infested" color={MENU_COLOR} size="text-3xl md:text-4xl lg:text-5xl" />

          {/* Stats row */}
          {connected && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex gap-3 mt-6"
            >
              <div className="px-4 py-2 text-center" style={{
                background: 'rgba(0,0,0,0.4)', border: `1px solid ${MENU_DIM}0.15)`, borderRadius: '2px',
              }}>
                <p className="text-[9px] tracking-wider uppercase" style={{ color: `${MENU_DIM}0.4)`, fontFamily: "'Courier New', monospace" }}>Balance</p>
                <p className="text-lg font-bold" style={{ color: MENU_COLOR, fontFamily: "'Courier New', monospace" }} data-testid="fees-balance">{userFeesBalance.toLocaleString()}</p>
              </div>
              <div className="px-4 py-2 text-center" style={{
                background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: '2px',
              }}>
                <p className="text-[9px] tracking-wider uppercase" style={{ color: 'rgba(245,158,11,0.4)', fontFamily: "'Courier New', monospace" }}>High Score</p>
                <p className="text-lg font-bold" style={{ color: '#f59e0b', fontFamily: "'Courier New', monospace" }} data-testid="high-score">{highScore.toLocaleString()}</p>
              </div>
            </motion.div>
          )}

          {!connected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6 px-6 py-4 text-center" style={{
                background: 'rgba(0,0,0,0.3)', border: `1px solid ${MENU_DIM}0.1)`, borderRadius: '2px',
              }}
            >
              <Wallet className="w-6 h-6 mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.15)' }} />
              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Courier New', monospace" }}>Connect wallet to save progress</p>
              <WalletMultiButton className="!bg-orange-500/10 !border !border-orange-500/25 !rounded-sm !py-2 !px-6" />
            </motion.div>
          )}

          {/* Difficulty Selector */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="w-full max-w-md mt-6 space-y-2"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-center mb-3"
               style={{ color: `${MENU_DIM}0.4)`, fontFamily: "'Courier New', monospace" }}>
              // THREAT LEVEL
            </p>
            {Object.entries(DIFFICULTY_SETTINGS).map(([key, diff], idx) => {
              const isSelected = difficulty === key;
              const isNightmare = key === 'nightmare';
              const accentColor = isNightmare ? MENU_COLOR : key === 'hard' ? '#cc5500' : MENU_COLOR;
              return (
                <motion.button
                  key={key}
                  onClick={() => setDifficulty(key)}
                  data-testid={`difficulty-${key}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + idx * 0.12 }}
                  className="w-full relative overflow-hidden text-left"
                  style={{
                    background: isSelected ? `linear-gradient(90deg, ${accentColor}12 0%, transparent 100%)` : 'rgba(0,0,0,0.3)',
                    border: `1px solid ${isSelected ? `${accentColor}50` : 'rgba(255,255,255,0.04)'}`,
                    borderRadius: '2px',
                    padding: '12px 16px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isSelected && (
                    <motion.div layoutId="whack-diff-indicator"
                      className="absolute left-0 top-0 bottom-0 w-0.5"
                      style={{
                        background: isNightmare ? `linear-gradient(180deg, ${MENU_COLOR}, #cc5500)` : accentColor,
                        boxShadow: `0 0 8px ${accentColor}`,
                      }}
                    />
                  )}
                  {isNightmare && isSelected && (
                    <motion.div className="absolute inset-0 pointer-events-none"
                      style={{ background: `linear-gradient(90deg, transparent, ${MENU_DIM}0.06), rgba(168,85,247,0.06), transparent)` }}
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                    />
                  )}
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isNightmare ? (
                        <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                          <Skull className="w-4 h-4" style={{ color: MENU_COLOR, filter: `drop-shadow(0 0 6px ${MENU_DIM}0.8))` }} />
                        </motion.div>
                      ) : (
                        <Zap className="w-4 h-4" style={{ color: isSelected ? accentColor : 'rgba(255,255,255,0.2)' }} />
                      )}
                      <div>
                        <span className="font-bold text-sm tracking-wider" style={{
                          color: isSelected ? accentColor : 'rgba(255,255,255,0.4)',
                          fontFamily: "'Courier New', monospace",
                          textShadow: isNightmare && isSelected ? `0 0 8px ${MENU_DIM}0.5)` : 'none',
                        }}>
                          {isNightmare ? (
                            <span style={{ background: `linear-gradient(90deg, ${MENU_COLOR}, #cc5500)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                              {diff.name.toUpperCase()}
                            </span>
                          ) : diff.name.toUpperCase()}
                        </span>
                        <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: "'Courier New', monospace" }}>
                          {diff.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold" style={{
                        color: isSelected ? accentColor : 'rgba(255,255,255,0.3)',
                        fontFamily: "'Courier New', monospace",
                      }}>
                        {diff.feesMultiplier}x
                      </span>
                      <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.15)' }}>multiplier</p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>

          {/* Start Button */}
          <motion.button
            onClick={startGame}
            data-testid="start-game-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            className="mt-6 px-10 py-4 font-bold text-base tracking-[0.15em] uppercase relative overflow-hidden"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: `1px solid ${MENU_DIM}0.4)`,
              color: MENU_COLOR,
              borderRadius: '2px',
              fontFamily: "'Courier New', monospace",
              boxShadow: `0 0 15px ${MENU_DIM}0.2), 0 0 30px ${MENU_DIM}0.1)`,
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="relative z-10 flex items-center gap-2">
              <Zap className="w-5 h-5" /> ENTER THE SEWERS
            </span>
            <motion.div
              className="absolute inset-0"
              style={{ background: `linear-gradient(90deg, transparent, ${MENU_DIM}0.08), transparent)` }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
          </motion.button>

          {/* How to Play */}
          <motion.button
            onClick={() => setShowHowToPlay(!showHowToPlay)}
            data-testid="how-to-play-btn"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mt-3 flex items-center gap-1.5 text-xs tracking-wider"
            style={{ color: `${MENU_DIM}0.3)`, fontFamily: "'Courier New', monospace" }}
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showHowToPlay ? 'rotate-180' : ''}`} />
            {showHowToPlay ? 'HIDE' : '// HOW TO PLAY'}
          </motion.button>

          <AnimatePresence>
            {showHowToPlay && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden w-full max-w-md">
                <div className="mt-3 p-4 text-sm space-y-2.5" style={{
                  background: 'rgba(0,0,0,0.5)', border: `1px solid ${MENU_DIM}0.1)`, borderRadius: '2px',
                }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Courier New', monospace" }}>
                    <span style={{ color: `${MENU_DIM}0.6)` }}>OBJECTIVE:</span> Click gremlins as they emerge. Earn fees and points.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <span className="flex items-center gap-1" style={{ color: 'rgba(255,140,0,0.6)' }}><img src={gasCanister} alt="" className="w-4 h-4" /> +100</span>
                    <span className="flex items-center gap-1" style={{ color: 'rgba(255,140,0,0.6)' }}><img src={goldenGremlin} alt="" className="w-4 h-4" /> +500</span>
                    <span className="flex items-center gap-1" style={{ color: 'rgba(6,182,212,0.6)' }}><img src={diamond} alt="" className="w-4 h-4" /> +1000</span>
                    <span className="flex items-center gap-1" style={{ color: 'rgba(239,68,68,0.6)' }}><img src={gasBomb} alt="" className="w-4 h-4" /> -300</span>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.25)', fontFamily: "'Courier New', monospace", fontSize: '11px' }}>
                    Chain hits for combo multipliers. Avoid bombs. Higher difficulty = more fees per hit.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="mt-6 w-full max-w-md"
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-center mb-2"
                 style={{ color: `${MENU_DIM}0.3)`, fontFamily: "'Courier New', monospace" }}>
                // TOP HARVESTERS
              </p>
              <div className="flex justify-center gap-3">
                {leaderboard.slice(0, 3).map((entry, idx) => (
                  <div key={idx} className="px-4 py-2 text-center" style={{
                    background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px',
                  }}>
                    <span className="text-sm font-bold" style={{
                      color: idx === 0 ? '#ff8c00' : idx === 1 ? '#9ca3af' : '#f97316',
                      fontFamily: "'Courier New', monospace",
                    }}>#{idx + 1}</span>
                    <p className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {entry.wallet_address?.slice(0, 4)}...{entry.wallet_address?.slice(-4)}
                    </p>
                    <p className="text-sm font-semibold" style={{ color: MENU_COLOR }}>
                      {entry.total_gas_earned?.toLocaleString() || '0'}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          <StatusBar text="SEWER NETWORK: ACTIVE // INFESTATION LEVEL: CRITICAL" color={MENU_COLOR} />
        </div>
      </div>
    );
  }

  // ============ PLAYING STATE ============
  if (gameState === 'playing') {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none"
           style={{ background: 'radial-gradient(ellipse at 50% 60%, #1a1505 0%, #0d0b02 40%, #030200 100%)' }}
           data-testid="whack-game-playing">
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes pipeGlow {
            0%, 100% { box-shadow: inset 0 0 15px rgba(0,0,0,0.8), 0 0 8px rgba(255,140,0,0.05); }
            50% { box-shadow: inset 0 0 20px rgba(0,0,0,0.9), 0 0 12px rgba(255,140,0,0.1); }
          }
          @keyframes itemEmerge {
            0% { transform: translateY(100%) scale(0.4); opacity: 0; }
            40% { transform: translateY(-10%) scale(1.1); opacity: 1; }
            60% { transform: translateY(5%) scale(0.95); }
            100% { transform: translateY(0%) scale(1); opacity: 1; }
          }
          @keyframes itemSink {
            0% { transform: translateY(0%) scale(1); opacity: 1; }
            100% { transform: translateY(100%) scale(0.3); opacity: 0; }
          }
          @keyframes hitBurst {
            0% { transform: scale(0.3); opacity: 1; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          @keyframes drainSwirl {
            0% { transform: rotate(0deg); opacity: 0.15; }
            100% { transform: rotate(360deg); opacity: 0.15; }
          }
          @keyframes emberPulse {
            0%, 100% { opacity: 0.35; }
            50% { opacity: 0.85; }
          }
          @keyframes emberPulseFast {
            0%, 100% { opacity: 0.55; transform: scale(0.95); }
            50% { opacity: 1; transform: scale(1.08); }
          }
          @keyframes smokeRise {
            0% { opacity: 0; transform: translate(-50%, 0) scale(0.5); }
            35% { opacity: 0.55; }
            100% { opacity: 0; transform: translate(-50%, -55%) scale(1.6); }
          }
          @keyframes sigilPulse {
            0%, 100% { opacity: 0.06; transform: translate(-50%,-50%) scale(1); }
            50% { opacity: 0.14; transform: translate(-50%,-50%) scale(1.04); }
          }
          @keyframes sigilRotate {
            0% { transform: translate(-50%,-50%) rotate(0deg); }
            100% { transform: translate(-50%,-50%) rotate(360deg); }
          }
          @keyframes runeFlicker {
            0%, 92%, 100% { opacity: 0.55; }
            93%, 96% { opacity: 0.18; }
            94%, 97% { opacity: 0.7; }
          }
        `}</style>
        <ScanlineOverlay />
        <GasParticles count={12} color="molten" />
        <ToxicDrips count={5} color="#ff8c00" />

        {/* ═══ CATHEDRAL-SEWER FLOOR ═══ */}
        {/* Stone tile texture */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `
            linear-gradient(0deg, rgba(120,85,30,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(120,85,30,0.04) 1px, transparent 1px),
            radial-gradient(ellipse at 50% 50%, rgba(40,28,12,0.4) 0%, transparent 65%)
          `,
          backgroundSize: '60px 60px, 60px 60px, 100% 100%',
        }} />

        {/* Cracked stone grain — tiny dark flecks */}
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: `
            radial-gradient(circle at 12% 28%, rgba(0,0,0,0.55) 0%, transparent 0.8%),
            radial-gradient(circle at 78% 14%, rgba(0,0,0,0.4) 0%, transparent 0.6%),
            radial-gradient(circle at 42% 78%, rgba(0,0,0,0.55) 0%, transparent 0.7%),
            radial-gradient(circle at 88% 62%, rgba(0,0,0,0.45) 0%, transparent 0.5%),
            radial-gradient(circle at 22% 88%, rgba(0,0,0,0.4) 0%, transparent 0.6%)
          `,
        }} />

        {/* Central rune sigil — slow rotating pentagram-esque mark */}
        <div className="absolute pointer-events-none" style={{
          left: '50%', top: '52%',
          width: 'min(72vh, 720px)', height: 'min(72vh, 720px)',
          transform: 'translate(-50%,-50%)',
          animation: 'sigilPulse 6s ease-in-out infinite',
        }}>
          <div className="absolute inset-0" style={{
            border: '1px solid rgba(255,140,0,0.18)',
            borderRadius: '50%',
            animation: 'sigilRotate 120s linear infinite',
          }}>
            <div className="absolute inset-[8%]" style={{ border: '1px dashed rgba(255,140,0,0.10)', borderRadius: '50%' }} />
            <div className="absolute inset-[18%]" style={{ border: '1px solid rgba(255,140,0,0.07)', borderRadius: '50%' }} />
            {/* 8 cardinal tick marks */}
            {[...Array(8)].map((_, i) => (
              <div key={i} className="absolute" style={{
                left: '50%', top: '0%',
                width: '1px', height: '14px',
                background: 'rgba(255,140,0,0.25)',
                transformOrigin: '50% calc(50vh)',
                transform: `translateX(-50%) rotate(${i * 45}deg)`,
              }} />
            ))}
          </div>
        </div>

        {/* Edge molten vignette - light leaking from below */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 120%, rgba(255,69,0,0.10) 0%, rgba(180,40,0,0.04) 30%, transparent 60%)',
        }} />

        {/* HUD Top Bar — Centered, clean */}
        <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center py-3 px-2"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.7) 60%, transparent 100%)',
          }}>
          <div className="flex items-center gap-3 md:gap-6" data-testid="game-stats-bar">
            <div className="text-center">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>SCORE</span>
              <span className="text-xl md:text-2xl font-black tracking-wide" style={{
                color: '#ff8c00',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                textShadow: '0 0 12px rgba(255,140,0,0.35)',
              }} data-testid="game-score">{score.toLocaleString()}</span>
            </div>
            <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.12)' }} />
            <div className="text-center">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>FEES</span>
              <span className="text-xl md:text-2xl font-black tracking-wide" style={{
                color: '#ff8c00',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                textShadow: '0 0 12px rgba(255,140,0,0.35)',
              }} data-testid="game-fees">+{feesEarned.toLocaleString()}</span>
            </div>
            <div className="w-px h-8" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="text-center min-w-[60px]">
              <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block font-bold" style={{ color: timeLeft <= 10 ? '#ff6464' : '#cccccc', fontFamily: "'Courier New', monospace", textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>TIME</span>
              <span className="text-xl md:text-2xl font-black tracking-wide" style={{
                color: timeLeft <= 10 ? '#ef4444' : 'rgba(255,255,255,0.7)',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                textShadow: timeLeft <= 10 ? '0 0 15px rgba(239,68,68,0.5)' : 'none',
                animation: timeLeft <= 5 ? 'warningPulse 0.5s ease-in-out infinite' : 'none',
              }} data-testid="game-timer">{timeLeft}s</span>
            </div>
            {combo > 1 && (
              <>
                <div className="w-px h-8" style={{ background: 'rgba(255,140,0,0.12)' }} />
                <motion.div
                  key={combo}
                  initial={{ scale: 1.5 }}
                  animate={{ scale: 1 }}
                  className="text-center"
                >
                  <span className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase block" style={{ color: combo >= 10 ? 'rgba(239,68,68,0.4)' : 'rgba(255,140,0,0.35)', fontFamily: "'Courier New', monospace" }}>COMBO</span>
                  <span className="text-xl md:text-2xl font-black tracking-wide" style={{
                    color: combo >= 10 ? '#ef4444' : '#ff8c00',
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    textShadow: `0 0 12px ${combo >= 10 ? 'rgba(239,68,68,0.5)' : 'rgba(255,140,0,0.4)'}`,
                  }}>{combo}x</span>
                </motion.div>
              </>
            )}
          </div>
        </div>

        {/* Main Game Grid */}
        <div className="relative z-10 flex items-center justify-center h-full pt-14 pb-16 px-4">
          <div className="grid grid-cols-3 gap-x-4 gap-y-3 sm:gap-x-6 sm:gap-y-5 md:gap-x-14 md:gap-y-8"
               data-testid="whack-game-board"
               style={{ maxWidth: '650px', width: '100%' }}>
            {gremlins.map((gremlin, index) => (
              <div key={index} className="relative flex items-center justify-center"
                   style={{ aspectRatio: '1' }}>
                {/* ═══ CARVED STONE PIT — lore-themed sewer drain ═══ */}
                {/* Stone tile shadow under the pit */}
                <div className="absolute pointer-events-none" style={{
                  inset: '-14%',
                  background: 'radial-gradient(ellipse at 50% 55%, rgba(40,28,12,0.55) 0%, rgba(20,14,6,0.25) 45%, transparent 75%)',
                  zIndex: 0,
                }} />

                <div
                  data-testid={`whack-hole-${index}`}
                  onMouseDown={(e) => whackGremlin(index, e)}
                  onTouchStart={(e) => { e.preventDefault(); whackGremlin(index, e); }}
                  className="relative w-full h-full cursor-pointer overflow-hidden"
                  style={{
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 50% 65%, #060300 0%, #030100 50%, #010000 100%)',
                    // Layered shadow → carved stone double-rim with iron lip
                    boxShadow: `
                      inset 0 6px 18px rgba(0,0,0,0.95),
                      inset 0 -2px 10px rgba(120,60,0,0.06),
                      inset 0 0 24px rgba(0,0,0,0.85),
                      0 0 0 2px rgba(80,55,18,0.55),
                      0 0 0 5px rgba(20,12,5,0.92),
                      0 0 0 6px rgba(120,85,30,0.45),
                      0 0 0 8px rgba(40,26,10,0.6),
                      0 4px 14px rgba(0,0,0,0.6),
                      0 0 18px ${gremlin ? gremlin.color + '40' : 'rgba(255,140,0,0.05)'}
                    `,
                    transition: 'box-shadow 0.35s ease',
                  }}
                >
                  {/* Molten lava floor — embers glowing at the bottom of the pit */}
                  <div className="absolute inset-1 rounded-full pointer-events-none overflow-hidden">
                    <div className="absolute left-0 right-0 bottom-0 h-3/5" style={{
                      background: 'radial-gradient(ellipse at 50% 100%, rgba(255,69,0,0.22) 0%, rgba(200,50,0,0.10) 35%, transparent 70%)',
                      animation: gremlin ? 'emberPulseFast 1s ease-in-out infinite' : 'emberPulse 4s ease-in-out infinite',
                    }} />
                    {/* Tiny ember speck deep in the pit */}
                    <div className="absolute" style={{
                      left: '50%', bottom: '12%',
                      width: '6px', height: '6px',
                      marginLeft: '-3px',
                      background: 'radial-gradient(circle, rgba(255,150,40,0.9) 0%, rgba(255,80,0,0.5) 40%, transparent 70%)',
                      borderRadius: '50%',
                      filter: 'blur(1px)',
                      animation: gremlin ? 'emberPulseFast 0.8s ease-in-out infinite' : 'emberPulse 3s ease-in-out infinite',
                    }} />
                  </div>

                  {/* Iron grate cross-bars — subtle, evoking a sewer drain when empty */}
                  {!gremlin && (
                    <div className="absolute inset-3 rounded-full pointer-events-none opacity-30" style={{
                      background: `
                        repeating-conic-gradient(from 22deg, transparent 0deg, transparent 40deg, rgba(80,55,20,0.35) 40deg, rgba(80,55,20,0.35) 50deg)
                      `,
                    }} />
                  )}

                  {/* Smoke wisp drifting up from empty pit */}
                  {!gremlin && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full">
                      <div style={{
                        position: 'absolute',
                        left: '50%', bottom: '15%',
                        width: '60%', height: '70%',
                        background: 'radial-gradient(ellipse at 50% 100%, rgba(180,140,80,0.10) 0%, rgba(140,100,60,0.04) 40%, transparent 70%)',
                        filter: 'blur(3px)',
                        animation: `smokeRise ${4 + (index % 3)}s ease-in-out infinite`,
                        animationDelay: `${(index * 0.7) % 3}s`,
                      }} />
                    </div>
                  )}

                  {/* Inner depth shadow — top lip darkening */}
                  <div className="absolute inset-0 rounded-full pointer-events-none" style={{
                    boxShadow: 'inset 0 8px 14px rgba(0,0,0,0.85), inset 0 -3px 8px rgba(120,50,0,0.08)',
                  }} />

                  {/* Active gremlin glow ring */}
                  {gremlin && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{
                        border: `2px solid ${gremlin.color}55`,
                        boxShadow: `inset 0 0 22px ${gremlin.color}20, 0 0 18px ${gremlin.color}30`,
                      }}
                    />
                  )}

                  {/* Item emerging from pipe */}
                  <AnimatePresence>
                    {gremlin && (
                      <motion.div
                        initial={{ y: '100%', scale: 0.4, opacity: 0 }}
                        animate={{ y: '-5%', scale: 1, opacity: 1 }}
                        exit={{ y: '80%', scale: 0.3, opacity: 0 }}
                        transition={{
                          type: 'spring',
                          damping: 14,
                          stiffness: 350,
                          exit: { duration: 0.2, ease: 'easeIn' },
                        }}
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      >
                        <img
                          src={gremlin.image}
                          alt={gremlin.name}
                          className="w-3/5 h-3/5 object-contain pointer-events-none"
                          draggable={false}
                          style={{
                            filter: `drop-shadow(0 0 8px ${gremlin.color}60) drop-shadow(0 2px 4px rgba(0,0,0,0.8))`,
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Hit burst effect */}
                  <AnimatePresence>
                    {lastHit?.index === index && (
                      <>
                        <motion.div
                          initial={{ scale: 0.3, opacity: 1 }}
                          animate={{ scale: 2.5, opacity: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.4 }}
                          className="absolute inset-0 rounded-full pointer-events-none z-20"
                          style={{
                            background: `radial-gradient(circle, ${lastHit.color}30 0%, transparent 70%)`,
                          }}
                        />
                        <motion.div
                          initial={{ opacity: 1, scale: 0.5, y: 0 }}
                          animate={{ opacity: 0, scale: 2, y: -50 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5 }}
                          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-30"
                        >
                          <span className="font-black text-lg md:text-2xl" style={{
                            color: lastHit.color,
                            textShadow: `0 0 15px ${lastHit.color}, 0 0 30px ${lastHit.color}80`,
                            fontFamily: "'Impact', 'Arial Black', sans-serif",
                            WebkitTextStroke: `1px ${lastHit.color}`,
                          }}>
                            {lastHit.points > 0 ? '+' : ''}{lastHit.points}
                          </span>
                          {lastHit.fees > 0 && (
                            <span className="text-xs font-bold" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace" }}>
                              +{lastHit.fees} fees
                            </span>
                          )}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                {/* Wrought-iron rivets — N/E/S/W cardinal points on the stone rim */}
                {[
                  { top: '-4%', left: '50%', mx: -6, my: 0 },
                  { top: '50%', left: '104%', mx: -12, my: -6 },
                  { top: '104%', left: '50%', mx: -6, my: -12 },
                  { top: '50%', left: '-4%', mx: 0, my: -6 },
                ].map((p, i) => (
                  <div key={i} className="absolute pointer-events-none" style={{
                    top: p.top, left: p.left,
                    width: 12, height: 12,
                    marginLeft: p.mx, marginTop: p.my,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 35% 30%, rgba(180,140,55,0.85) 0%, rgba(110,75,25,0.9) 45%, rgba(50,32,10,1) 100%)',
                    boxShadow: 'inset 0 1px 2px rgba(255,200,100,0.35), inset 0 -1px 2px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.6)',
                    zIndex: 2,
                  }} />
                ))}

                {/* Carved Roman numeral plaque — anchored to the bottom rim */}
                <div className="absolute pointer-events-none" style={{
                  bottom: '-10%', left: '50%',
                  transform: 'translateX(-50%)',
                  fontFamily: "'Cinzel', serif",
                  fontSize: 'clamp(0.5rem, 1vw, 0.65rem)',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: gremlin ? `${gremlin.color}` : 'rgba(160,115,40,0.55)',
                  textShadow: gremlin
                    ? `0 0 8px ${gremlin.color}, 0 1px 2px rgba(0,0,0,0.9)`
                    : '0 1px 2px rgba(0,0,0,0.85)',
                  background: 'linear-gradient(180deg, rgba(18,11,4,0.95) 0%, rgba(8,5,2,0.95) 100%)',
                  padding: '2px 9px',
                  border: '1px solid rgba(120,85,30,0.45)',
                  borderRadius: 1,
                  boxShadow: 'inset 0 1px 1px rgba(180,140,55,0.18), 0 1px 3px rgba(0,0,0,0.7)',
                  transition: 'color 0.3s, text-shadow 0.3s',
                  zIndex: 3,
                }}>
                  {PIT_NUMERALS[index]}
                </div>
              </div>
            ))}
          </div>
        </div>

        <TotalFeesCounter fees={feesEarned} color="#ff8c00" />

        {/* Bottom legend bar */}
        <div className="absolute bottom-3 left-0 right-0 z-30 flex justify-center gap-3 md:gap-5 text-xs px-4"
          style={{ fontFamily: "'Courier New', monospace" }}>
          {[
            { img: gasCanister, pts: '+100', color: 'rgba(255,140,0,0.5)' },
            { img: goldenGremlin, pts: '+500', color: 'rgba(255,140,0,0.5)' },
            { img: diamond, pts: '+1000', color: 'rgba(6,182,212,0.5)' },
            { img: gasBomb, pts: '-300', color: 'rgba(239,68,68,0.5)' },
          ].map(({ img, pts, color }, i) => (
            <div key={i} className="flex items-center gap-1.5" style={{ color }}>
              <img src={img} alt="" className="w-5 h-5 opacity-70" />
              <span>{pts}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============ ENDED STATE ============
  if (gameState === 'ended') {
    const accuracy = hits.total > 0 ? Math.round((hits.correct / hits.total) * 100) : 0;
    const isNewHighScore = score > highScore;
    
    return (
      <div className="relative h-screen w-full overflow-hidden"
           style={{
             background: 'radial-gradient(ellipse at 50% 40%, #1a1a05 0%, #0d0d02 40%, #030200 100%)',
             animation: endedPhase === 'victory' ? 'screenShake 0.5s ease-out' : 'none',
           }}
           data-testid="whack-game-ended">
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes screenShake {
            0%, 100% { transform: translate(0,0); }
            10% { transform: translate(-6px, 4px); }
            20% { transform: translate(4px, -6px); }
            30% { transform: translate(-4px, 2px); }
            40% { transform: translate(2px, -2px); }
            50% { transform: translate(-2px, 4px); }
          }
          @keyframes victoryFlash {
            0% { opacity: 1; background: white; }
            20% { opacity: 0.8; background: #ff8c00; }
            50% { opacity: 0.3; background: #ff8c00; }
            100% { opacity: 0; }
          }
          @keyframes victorySlam {
            0% { transform: scale(4) rotate(-10deg); opacity: 0; letter-spacing: 0.6em; }
            30% { transform: scale(1.15) rotate(2deg); opacity: 1; letter-spacing: 0.15em; }
            50% { transform: scale(0.95) rotate(-1deg); }
            70% { transform: scale(1.02) rotate(0deg); }
            100% { transform: scale(1) rotate(0deg); letter-spacing: 0.2em; opacity: 1; }
          }
          @keyframes victoryRays {
            0% { opacity: 0; transform: scale(0.3) rotate(0deg); }
            30% { opacity: 0.7; transform: scale(1) rotate(15deg); }
            100% { opacity: 0.1; transform: scale(1.3) rotate(30deg); }
          }
          @keyframes feeCountUp {
            0% { transform: scale(0.5); opacity: 0; }
            50% { transform: scale(1.15); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes sparkFloat {
            0% { transform: translateY(0) scale(1); opacity: 1; }
            100% { transform: translateY(-60px) scale(0); opacity: 0; }
          }
          @keyframes borderGlow {
            0%, 100% { border-color: rgba(255,140,0,0.3); box-shadow: inset 0 0 40px rgba(255,140,0,0.05); }
            50% { border-color: rgba(255,140,0,0.5); box-shadow: inset 0 0 60px rgba(255,140,0,0.1); }
          }
        `}</style>

        {/* === PHASE 1: VICTORY CINEMATIC === */}
        {endedPhase === 'victory' && (
          <>
            {/* White → Gold flash */}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 100,
              animation: 'victoryFlash 0.7s ease-out forwards',
              pointerEvents: 'none',
            }} />

            {/* Radial rays */}
            <div style={{
              position: 'absolute', inset: '-50%', zIndex: 40,
              animation: 'victoryRays 1.5s ease-out forwards',
              pointerEvents: 'none',
            }}>
              {[...Array(24)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: '200%', height: '3px',
                  background: `linear-gradient(90deg, transparent 30%, rgba(255,140,0,${0.15 + Math.random() * 0.2}) 50%, transparent 70%)`,
                  transformOrigin: '0% 50%',
                  transform: `rotate(${i * 15}deg)`,
                }} />
              ))}
            </div>

            {/* Floating sparks */}
            {[...Array(12)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${15 + Math.random() * 70}%`,
                top: `${30 + Math.random() * 40}%`,
                width: '4px', height: '4px',
                borderRadius: '50%',
                background: i % 3 === 0 ? '#ff8c00' : i % 3 === 1 ? '#ffd700' : '#ff4500',
                animation: `sparkFloat ${0.8 + Math.random() * 1.2}s ${i * 0.1}s ease-out forwards`,
                zIndex: 60, pointerEvents: 'none',
              }} />
            ))}

            {/* Main text block */}
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center pointer-events-none">
              <div style={{
                fontSize: 'clamp(40px, 10vw, 80px)',
                fontWeight: 900,
                color: '#ff8c00',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                animation: 'victorySlam 0.7s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both',
                textShadow: '0 0 25px rgba(255,140,0,0.5), 0 0 60px rgba(255,140,0,0.2), 0 4px 0 #92710a, 0 8px 0 #5c4706',
                WebkitTextStroke: '2px #a07c0a',
                letterSpacing: '0.2em',
              }}>
                ROUND CLEAR
              </div>

              {isNewHighScore && (
                <div style={{
                  fontSize: 'clamp(14px, 3vw, 22px)',
                  color: '#f59e0b',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  animation: 'victorySlam 0.6s 0.5s cubic-bezier(0.22, 1, 0.36, 1) both',
                  letterSpacing: '0.3em',
                  textShadow: '0 0 12px rgba(245,158,11,0.4)',
                }}>
                  NEW HIGH SCORE
                </div>
              )}

              <div style={{
                fontSize: 'clamp(28px, 7vw, 56px)',
                fontWeight: 900,
                color: '#ffffff',
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                animation: 'feeCountUp 0.5s 0.8s cubic-bezier(0.22, 1, 0.36, 1) both',
                textShadow: '0 0 20px rgba(255,255,255,0.2)',
              }}>
                {score.toLocaleString()}
              </div>

              {feesEarned > 0 && (
                <>
                <div style={{
                  fontSize: 'clamp(16px, 4vw, 28px)',
                  fontWeight: 900,
                  color: '#ff8c00',
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  animation: 'feeCountUp 0.5s 1.1s cubic-bezier(0.22, 1, 0.36, 1) both',
                  textShadow: '0 0 15px rgba(255,140,0,0.4)',
                  letterSpacing: '0.1em',
                }}>
                  +{feesEarned.toLocaleString()} FEES STOLEN
                </div>
                <div style={{
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.85rem)',
                  color: '#ff6b00',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.15em',
                  opacity: 0.7,
                  marginTop: '4px',
                }}>
                  +15 SP
                </div>
                </>
              )}
            </div>
          </>
        )}

        {/* === PHASE 2: STATS === */}
        <AnimatePresence>
          {endedPhase === 'stats' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 z-30"
            >
              <ScanlineOverlay />
              <GasParticles count={8} color="molten" />

              {/* Animated border frame */}
              <div className="absolute inset-3 pointer-events-none" style={{
                border: '2px solid rgba(255,140,0,0.3)',
                animation: 'borderGlow 3s ease-in-out infinite',
              }} />

              <div className="relative z-20 flex flex-col items-center justify-center h-full px-4 overflow-y-auto py-8">
                {/* Score header */}
                <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                  className="text-center mb-5">
                  {isNewHighScore && (
                    <div className="flex items-center gap-2 mb-1 justify-center">
                      <Star className="w-4 h-4" style={{ color: '#ff8c00', filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.6))' }} />
                      <span className="text-xs font-bold tracking-widest" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace" }}>NEW HIGH SCORE</span>
                      <Star className="w-4 h-4" style={{ color: '#ff8c00', filter: 'drop-shadow(0 0 6px rgba(255,140,0,0.6))' }} />
                    </div>
                  )}
                  <p className="text-4xl font-black" style={{
                    color: '#ff8c00',
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    textShadow: '0 0 20px rgba(255,140,0,0.3)',
                    letterSpacing: '0.05em',
                  }} data-testid="final-score">{score.toLocaleString()}</p>
                </motion.div>

                {/* Stats grid */}
                <div className="grid grid-cols-4 gap-3 mb-4 w-full max-w-md">
                  {[
                    { icon: Flame, label: 'FEES', value: `+${feesEarned.toLocaleString()}`, color: '#ff8c00', delay: 0.15 },
                    { icon: Target, label: 'SEASON SP', value: '+15', color: '#ff6b00', delay: 0.2 },
                    { icon: Zap, label: 'MAX COMBO', value: `${maxCombo}x`, color: '#ff8c00', delay: 0.25 },
                    { icon: Shield, label: 'ACCURACY', value: `${accuracy}%`, color: 'rgba(255,255,255,0.5)', delay: 0.35 },
                  ].map(({ icon: Icon, label, value, color, delay }) => (
                    <motion.div key={label} initial={{ y: 15, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay }}
                      className="p-3 text-center" style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${color}15`, borderRadius: '2px' }}>
                      <Icon className="w-5 h-5 mx-auto mb-1" style={{ color, opacity: 0.6 }} />
                      <p className="text-xl font-bold" style={{ color, fontFamily: "'Courier New', monospace" }}>{value}</p>
                      <p className="text-[9px] tracking-wider" style={{ color: 'rgba(255,255,255,0.2)', fontFamily: "'Courier New', monospace" }}>{label}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Hit Breakdown */}
                <motion.div initial={{ y: 15, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                  className="w-full max-w-md p-4 mb-4" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '2px' }}>
                  <p className="text-[9px] tracking-widest uppercase mb-2" style={{ color: 'rgba(255,255,255,0.2)', fontFamily: "'Courier New', monospace" }}>HIT BREAKDOWN</p>
                  {[
                    { label: 'Total Hits', value: hits.total, color: 'rgba(255,255,255,0.5)' },
                    { label: 'Correct', value: hits.correct, color: '#ff8c00' },
                    { label: 'Penalties', value: hits.penalty, color: '#ef4444' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="flex justify-between text-sm mb-1" style={{ fontFamily: "'Courier New', monospace" }}>
                      <span style={{ color: `${color}80` }}>{label}</span>
                      <span style={{ color }}>{value}</span>
                    </div>
                  ))}
                </motion.div>

                {/* Claim Fees */}
                {!connected && feesEarned > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                    className="w-full max-w-md p-3 mb-4 text-center" style={{ background: 'rgba(255,140,0,0.05)', border: '1px solid rgba(255,140,0,0.15)', borderRadius: '2px' }}>
                    <p className="text-sm" style={{ color: 'rgba(255,140,0,0.6)', fontFamily: "'Courier New', monospace" }}>Connect wallet to claim fees</p>
                  </motion.div>
                )}

                {connected && feesEarned > 0 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                    className="w-full max-w-md mb-4">
                    {feesClaimed ? (
                      <div className="w-full py-3 text-center" style={{ background: 'rgba(255,140,0,0.05)', border: '1px solid rgba(255,140,0,0.2)', borderRadius: '2px' }}>
                        <p className="font-bold text-sm flex items-center justify-center gap-2" style={{ color: '#ff8c00', fontFamily: "'Courier New', monospace" }}>
                          <Flame className="w-4 h-4" /> +{feesEarned.toLocaleString()} FEES CLAIMED
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: '#ff6b00', fontFamily: "'Courier New', monospace" }}>+15 Season Points</p>
                        {onchainReceipt && (
                          <div className="mt-3 flex justify-center">
                            <OnChainReceipt receipt={onchainReceipt} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <button onClick={claimFees} disabled={claiming} data-testid="claim-fees-btn"
                        className="w-full py-3.5 font-bold transition-all flex items-center justify-center gap-2 text-base"
                        style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.4)', color: '#ff8c00', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                        {claiming ? (
                          <motion.div className="w-5 h-5 border-2 border-orange-400/30 border-t-orange-400 rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} />
                        ) : (
                          <><Flame className="w-5 h-5" /> CLAIM {feesEarned.toLocaleString()} FEES</>
                        )}
                      </button>
                    )}
                  </motion.div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3 w-full max-w-md">
                  <button onClick={() => setGameState('menu')} data-testid="back-to-menu-btn"
                    className="flex-1 py-3 font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                    <ChevronRight className="w-4 h-4 rotate-180" />MENU
                  </button>
                  <button onClick={startGame} data-testid="play-again-btn"
                    className="flex-1 py-3 font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: 'rgba(255,140,0,0.05)', border: '1px solid rgba(255,140,0,0.2)', color: 'rgba(255,140,0,0.7)', borderRadius: '2px', fontFamily: "'Courier New', monospace" }}>
                    <RotateCcw className="w-4 h-4" />AGAIN
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
}
